import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, MessageCircle, ArrowLeft, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { safeFetch } from "../utils/safeFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "방금 전";
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}일 전`;
  return new Date(iso).toLocaleDateString("ko-KR", { month: "short", day: "numeric" });
}

const CATEGORY_LABELS = {
  home: "홈피드",
  relationships: "연애 & 인간관계",
  travel: "여행",
  health: "건강",
  hobby: "취미",
  retirement: "노후 & 인생2막",
  free: "자유게시판",
  today_moment: "오늘의 하루",
  anonymous_advice: "익명 고민상담",
};

export default function LikedPostsPage() {
  const { token, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (authLoading) return; // wait until auth hydration completes
    if (!token) {
      navigate("/", { replace: true });
      return;
    }
    const load = async () => {
      try {
        const res = await safeFetch(`${API_URL}/api/posts/mine/liked?limit=500`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("좋아요한 글을 불러오지 못했습니다.");
        const data = await res.json();
        setPosts(data.posts || []);
        setTotal(data.total || 0);
      } catch (e) {
        setError(e.message || "오류가 발생했습니다.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, navigate, authLoading]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const p of posts) {
      const d = new Date(p.created_at || 0);
      const key = `${d.getFullYear()}년 ${d.getMonth() + 1}월`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    return Array.from(map.entries());
  }, [posts]);

  return (
    <div className="min-h-screen bg-stone-50 pt-20 pb-24" data-testid="liked-posts-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-stone-500 hover:text-stone-800 text-sm"
            data-testid="liked-posts-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> 뒤로
          </button>
          {user && (
            <span className="text-xs text-stone-400">{user.username}님의 좋아요</span>
          )}
        </div>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center text-white">
            <Heart className="w-5 h-5" fill="currentColor" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-stone-900">좋아요한 글</h1>
            <p className="text-sm text-stone-500 mt-0.5">
              총 <span className="font-semibold text-stone-700">{total}</span>개의 글에 공감을 표현하셨어요
            </p>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="py-16 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700" data-testid="liked-posts-error">
            {error}
          </div>
        ) : posts.length === 0 ? (
          <div className="bg-white rounded-2xl border border-stone-100 py-16 px-6 text-center" data-testid="liked-posts-empty">
            <Heart className="w-10 h-10 text-stone-300 mx-auto mb-3" />
            <p className="font-serif text-lg text-stone-600">아직 좋아요한 글이 없어요.</p>
            <p className="text-sm text-stone-400 mt-2">
              마음에 드는 이야기에 ❤️를 눌러보세요. 이곳에 모아드려요.
            </p>
            <button
              onClick={() => navigate("/community")}
              className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-burgundy-600 hover:bg-burgundy-700 text-white text-sm font-semibold"
              data-testid="liked-posts-explore-btn"
            >
              커뮤니티 둘러보기 →
            </button>
          </div>
        ) : (
          <div className="space-y-10">
            {groups.map(([month, items]) => (
              <section key={month}>
                <h2 className="font-serif text-base text-stone-500 mb-3 pl-1 flex items-center gap-2">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-400" />
                  {month}
                  <span className="text-[11px] text-stone-400 font-sans">· {items.length}개</span>
                </h2>
                <ul className="space-y-3">
                  {items.map((p) => {
                    const cat = CATEGORY_LABELS[p.category] || p.category || "이야기";
                    const firstImg = p.images?.[0];
                    return (
                      <li key={p.id}>
                        <button
                          onClick={() => navigate(`/community/post/${p.id}`)}
                          className="w-full text-left bg-white rounded-2xl border border-stone-100 p-4 sm:p-5 hover:border-rose-200 hover:shadow-[0_2px_12px_rgba(244,63,94,0.06)] transition-all group"
                          data-testid={`liked-post-${p.id}`}
                        >
                          <div className="flex items-center gap-2 text-[11px] text-stone-400 mb-2">
                            <span className="text-rose-500 font-medium">{cat}</span>
                            <span>·</span>
                            <span>{timeAgo(p.created_at)}</span>
                            <span>·</span>
                            <span>{p.anonymous ? "익명" : (p.author || "—")}</span>
                          </div>
                          <h3 className="font-semibold text-stone-900 group-hover:text-burgundy-700 line-clamp-1">
                            {p.title || "(제목 없음)"}
                          </h3>
                          {p.content && (
                            <p className="text-sm text-stone-600 mt-1.5 line-clamp-2 leading-relaxed">
                              {p.content}
                            </p>
                          )}
                          {firstImg && (
                            <div className="mt-3">
                              <img
                                src={firstImg.startsWith("http") ? firstImg : `${API_URL}${firstImg}`}
                                alt=""
                                className="w-full max-h-48 object-cover rounded-xl border border-stone-100"
                              />
                            </div>
                          )}
                          <div className="flex items-center gap-4 text-xs text-stone-400 mt-3">
                            <span className="inline-flex items-center gap-1">
                              <Heart className="w-3.5 h-3.5 text-rose-400" fill="currentColor" /> {p.likes || 0}
                            </span>
                            <span className="inline-flex items-center gap-1">
                              <MessageCircle className="w-3.5 h-3.5" /> {p.comments_count || 0}
                            </span>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
